import { useState, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { NotificationList } from '@/components/navbar/NotificationList'
import { useClickOutside } from '@/hooks/ui/useClickOutside'
import {
  useNotifications,
  useUnreadCount,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
} from '@/hooks/useNotifications'
import { usePushSubscription } from '@/hooks/usePushSubscription'
import { BellIcon } from '@/icons'
import type { Notification } from '@/types'

function UnreadBadge({ count }: { count: number }) {
  return (
    <span
      aria-label={`${count} unread notification${count === 1 ? '' : 's'}`}
      className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-xs font-bold text-white bg-red-500 rounded-full"
    >
      {count > 99 ? '99+' : count}
    </span>
  )
}

/** Opt-in prompt + error line for web push, shown above the list. */
function PushPrompt({
  permission,
  error,
  onEnable,
}: {
  permission: string
  error: string | null
  onEnable: () => void
}) {
  return (
    <>
      {permission === 'default' && (
        <button
          onClick={onEnable}
          className="w-full px-3 py-2 text-sm text-blue-400 hover:bg-hover border-b border-theme-border text-left flex items-center gap-2"
        >
          <BellIcon className="w-4 h-4" />
          Enable push notifications
        </button>
      )}
      {error && <div className="px-3 py-2 text-sm text-red-400 border-b border-theme-border">{error}</div>}
    </>
  )
}

export default function NotificationBell() {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  const { data: unreadData } = useUnreadCount()
  const { data: notificationsData, isLoading } = useNotifications(10, { enabled: isOpen })
  const markRead = useMarkNotificationRead()
  const markAllRead = useMarkAllNotificationsRead()
  const { permission, subscribe } = usePushSubscription()
  const [pushError, setPushError] = useState<string | null>(null)

  const unreadCount = unreadData?.unreadCount || 0

  useClickOutside(dropdownRef, () => setIsOpen(false), isOpen)

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read) {
      markRead.mutate({ id: notification.id })
    }

    if (notification.url) {
      navigate(notification.url)
    }

    setIsOpen(false)
  }

  const handleEnablePush = async () => {
    try {
      setPushError(null)
      await subscribe()
    } catch {
      setPushError('Failed to enable push notifications')
    }
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-muted hover:text-primary transition-colors"
        aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      >
        <BellIcon className="w-5 h-5" />
        {unreadCount > 0 && <UnreadBadge count={unreadCount} />}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-elevated border border-theme-border rounded-lg shadow-xl z-50">
          <div className="p-3 border-b border-theme-border flex items-center justify-between">
            <h3 className="font-semibold text-primary">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={() => markAllRead.mutate()}
                disabled={markAllRead.isPending}
                className="text-sm text-blue-400 hover:text-blue-300"
              >
                Mark all read
              </button>
            )}
          </div>

          <PushPrompt permission={permission} error={pushError} onEnable={handleEnablePush} />

          <NotificationList
            notifications={notificationsData?.notifications || []}
            isLoading={isLoading}
            onSelect={handleNotificationClick}
          />

          <div className="p-2 border-t border-theme-border">
            <Link
              to="/subscriptions"
              onClick={() => setIsOpen(false)}
              className="block text-center text-sm text-blue-400 hover:text-blue-300 py-1"
            >
              View all subscriptions
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}
